import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Space, message, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { usePermissions } from '../../contexts/PermissionContext';
import Api from '../../Network';
import NameByLang from '../../Component/nameByLang';
import './style.scss';

const { Option } = Select;

interface CustomerClassificationModalProps {
  visible: boolean;
  customer: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const CustomerClassificationModal: React.FC<CustomerClassificationModalProps> = ({
  visible,
  customer,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Fetch customer types
  const { data: customerTypesData, isLoading: customerTypesLoading } = useQuery({
    queryKey: ['customerTypes'],
    queryFn: async () => {
      const response = await Api.get('/api/admin/customer-types');
      return response;
    },
    enabled: visible,
  });

  // Fetch payment methods
  const { data: paymentMethodsData, isLoading: paymentMethodsLoading } = useQuery({
    queryKey: ['paymentMethods'],
    queryFn: async () => {
      const response = await Api.get('/api/admin/payment-methods');
      return response;
    },
    enabled: visible,
  });

  // Fetch sales representatives (drivers)
  const { data: salesRepsData, isLoading: salesRepsLoading } = useQuery({
    queryKey: ['salesRepresentatives'],
    queryFn: async () => {
      const response = await Api.get('/api/admin/drivers', {
        headers: {
          'per-page': '1000',
        },
      });
      return response;
    },
    enabled: visible,
  });

  // Fetch all classifications to check for existing matches
  const { data: classificationsData } = useQuery({
    queryKey: ['customerClassifications', 'all'],
    queryFn: async () => {
      const response = await Api.get('/api/admin/customer-classifications', {
        headers: {
          'per-page': '1000',
        },
      });
      return response;
    },
    enabled: visible,
  });

  // Create classification mutation
  const createClassificationMutation = useMutation({
    mutationFn: (data: any) => Api.post('/api/admin/customer-classifications', data),
  });

  // Update customer classification mutation
  const updateCustomerClassificationMutation = useMutation({
    mutationFn: (data: any) => Api.put(`/api/admin/customers/${customer?.id}`, data),
    onSuccess: () => {
      message.success(
        t('customers.classificationUpdateSuccess', 'Customer classification updated successfully')
      );
      form.resetFields();
      onSuccess?.();
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 422) {
        const errors = error?.response?.data?.errors || {};
        Object.keys(errors).forEach((key) => {
          form.setFields([
            {
              name: key,
              errors: Array.isArray(errors[key]) ? errors[key] : [errors[key]],
            },
          ]);
        });
      } else {
        const errorMessage =
          error?.response?.data?.message ||
          t('common.error', 'Error updating customer classification');
        message.error(errorMessage);
      }
    },
  });

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const { sales_representative_id, payment_method_id, customer_type_id } = values;

      // If all three are null/undefined, just set classification to null
      if (!sales_representative_id && !payment_method_id && !customer_type_id) {
        await updateCustomerClassificationMutation.mutateAsync({
          customer_classification_id: null,
        });
        return;
      }

      // Find existing classification that matches all three factors
      const classifications = classificationsData?.data?.data || [];
      const existingClassification = classifications.find((classification: any) => {
        const matchesSalesRep =
          (!sales_representative_id && !classification.sales_representative_id) ||
          classification.sales_representative_id === sales_representative_id;
        const matchesPaymentMethod =
          (!payment_method_id && !classification.payment_method_id) ||
          classification.payment_method_id === payment_method_id;
        const matchesCustomerType =
          (!customer_type_id && !classification.customer_type_id) ||
          classification.customer_type_id === customer_type_id;

        return matchesSalesRep && matchesPaymentMethod && matchesCustomerType;
      });

      let classificationId = null;

      if (existingClassification) {
        // Use existing classification
        classificationId = existingClassification.id;
      } else {
        // Check permission before creating new classification
        const canCreate =
          hasPermission('customer_classifications.create') ||
          hasPermission('customer_classifications.update') ||
          hasPermission('customers.update');

        if (!canCreate) {
          message.error(
            t('permissions.accessDenied', 'You do not have permission to perform this action')
          );
          return;
        }

        // Create new classification
        const newClassification = await createClassificationMutation.mutateAsync({
          sales_representative_id: sales_representative_id || null,
          payment_method_id: payment_method_id || null,
          customer_type_id: customer_type_id || null,
        });
        classificationId = newClassification.data?.data?.id || newClassification.data?.id;
      }

      // Update customer with classification ID
      await updateCustomerClassificationMutation.mutateAsync({
        customer_classification_id: classificationId,
      });
    } catch (error) {
      console.error('Error setting classification:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Set form values when modal opens
  useEffect(() => {
    if (visible && customer?.customer_classification) {
      const classification = customer.customer_classification;
      form.setFieldsValue({
        sales_representative_id: classification.sales_representative_id || undefined,
        payment_method_id: classification.payment_method_id || undefined,
        customer_type_id: classification.customer_type_id || undefined,
      });
    } else if (visible) {
      form.resetFields();
    }
  }, [visible, customer, form]);

  const customerTypes = customerTypesData?.data?.data || [];
  const paymentMethods = paymentMethodsData?.data?.data || [];
  const salesReps = salesRepsData?.data?.data || [];

  return (
    <Modal
      title={t('customers.editClassification', 'Edit Customer Classification')}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="sales_representative_id"
          label={t('customers.columns.salesRepresentative', 'Sales Representative')}
        >
          <Select
            placeholder={t('customers.selectSalesRepresentative', 'Select sales representative')}
            loading={salesRepsLoading}
            allowClear
          >
            {salesReps.map((salesRep: any) => (
              <Option key={salesRep.id} value={salesRep.id}>
                {salesRep.name || `ID: ${salesRep.id}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="payment_method_id"
          label={t('customers.columns.paymentMethod', 'Payment Method')}
        >
          <Select
            placeholder={t('customers.selectPaymentMethod', 'Select payment method')}
            loading={paymentMethodsLoading}
            allowClear
          >
            {paymentMethods.map((paymentMethod: any) => (
              <Option key={paymentMethod.id} value={paymentMethod.id}>
                <NameByLang name_en={paymentMethod.name_en} name_ar={paymentMethod.name_ar} />
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="customer_type_id"
          label={t('customers.columns.customerType', 'Customer Type')}
        >
          <Select
            placeholder={t('customers.selectCustomerType', 'Select customer type')}
            loading={customerTypesLoading}
            allowClear
          >
            {customerTypes.map((customerType: any) => (
              <Option key={customerType.id} value={customerType.id}>
                <NameByLang name_en={customerType.name_en} name_ar={customerType.name_ar} />
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>{t('common.cancel', 'Cancel')}</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t('common.update', 'Update')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CustomerClassificationModal;
